<template>
  <ReportWrapper>
    <template #actions>
      <div class="flex items-start gap-x-2">
        <div class="flex flex-col gap-y-2">
          <div class="flex gap-x-2 items-start">
            <CustomerAutocomplete
              v-model="selectedCustomer"
              class="w-2/6 x-sm:w-full"
              label=""
              placeholder="Select customers"
              customer
              multiple
              hide-details
              :return-object="false"
              item-value="accountNumber"
            />

            <CfsSelect
              v-model="dateField"
              placeholder="Select date field"
              :items="dateFieldsLabels"
              class="w-2/6 x-sm:w-full"
              :rules="[rules.required]"
              @input="onChangeDatePickerMode"
            />
          </div>

          <div class="flex gap-x-2 items-start">
            <CfsSelect
              v-model="datePickerMode"
              placeholder="Select date mode"
              :items="datePickerModes"
              class="w-2/6 x-sm:w-full"
              @input="onChangeDatePickerMode"
            />
            <CfsDatePicker
              v-model="selectedDate"
              :placeholder="`Select ${datePickerMode}`"
              :mode="datePickerMode"
              :format="datePickerFormat"
              @update:formattedValue="formattedDate = $event"
            />
          </div>
        </div>

        <v-btn color="primary" :loading="isLoading" @click="download">
          <v-icon size="1rem" class="mr-1">mdi-tray-arrow-down</v-icon>
          <span>Download</span>
        </v-btn>
      </div>
    </template>
  </ReportWrapper>
</template>

<script lang="ts">
import { computed, defineAsyncComponent, defineComponent, ref } from 'vue';
import { useReports } from '@/composables/reports/useReports';
import { downloadFile } from '@/helpers/download';
import { useToast } from '@/composables/useToast';
import ReportWrapper from '@/components/reports/ReportWrapper.vue';
import { AxiosError } from 'axios';
import rules from '@/helpers/rules';

enum DatePickerMode {
  Quarter = 'quarter',
  Month = 'month',
  Year = 'year',
}

export default defineComponent({
  name: 'HblLotKpiReport',
  components: {
    ReportWrapper,
    CustomerAutocomplete: defineAsyncComponent(
      () => import('@/components/autocomplete/CustomerAutocompleteComponent.vue')
    ),
  },
  setup() {
    const toast = useToast();

    const selectedCustomer = ref<string | null>(null);
    const datePickerMode = ref<DatePickerMode>(DatePickerMode.Quarter);
    const selectedDate = ref<any>();

    const datePickerModes = Object.values(DatePickerMode);
    const datePickerFormats = {
      [DatePickerMode.Quarter]: 'YYYY-MM',
      [DatePickerMode.Month]: 'YYYY-MM',
      [DatePickerMode.Year]: 'YYYY',
    };

    const dateField = ref<string | null>(null);
    const dateFields = {
      createdAt: 'MBL Entry Date',
      eta: 'ETA',
      dischargeDate: 'Terminal Discharge Date',
      availableDate: 'CFS Available Date',
      stripDate: 'Strip Date',
    };
    const dateFieldsLabels = Object.values(dateFields);

    const formattedDate = ref<string | null>(null);

    const datePickerFormat = computed(() => datePickerFormats[datePickerMode.value]);

    const { useDownloadHblLotKpiReport } = useReports();
    const { mutateAsync: downloadReport, isLoading } = useDownloadHblLotKpiReport();

    const onChangeDatePickerMode = (value: string) => {
      selectedDate.value = null;
      formattedDate.value = null;
    };

    const download = async () => {
      const defaultCustomersIds = 'all';

      if (!dateField.value) return toast.info('Please select date field.');

      if (!formattedDate.value) return toast.info('Please select date period.');

      try {
        const data = await downloadReport({
          customer: selectedCustomer.value || defaultCustomersIds,
          type: datePickerMode.value,
          value: formattedDate.value,
          dateField: Object.entries(dateFields).find(([, value]) => value === dateField.value)?.[0],
        });

        if (!data) return;

        downloadFile(data.base64Data, data.filename ?? 'Customer Invoices Report');
      } catch (error) {
        const axiosError = error as AxiosError<string>;
        toast.error(axiosError?.response?.data!);
      }
    };

    return {
      selectedDate,
      selectedCustomer,
      isLoading,
      download,
      datePickerMode,
      datePickerModes,
      datePickerFormat,
      formattedDate,
      onChangeDatePickerMode,
      rules,
      dateField,
      dateFieldsLabels,
    };
  },
});
</script>
